import { useI18n, type MessageKey, type Translate } from '../i18n';
import { nextThemePref, type Scheme, type ThemePref } from '../theme/theme';
import { Icon } from '../ui/Icon';

const MODE: Record<ThemePref, MessageKey> = { light: 'theme.light', dark: 'theme.dark', system: 'theme.system' };

/** « Thème : clair » / "Theme: dark": the accessible name of the toggle and of its menu item. */
export function themeLabel(t: Translate, pref: ThemePref): string {
  return t('theme.label', { mode: t(MODE[pref]) });
}

/**
 * Header toggle, light → dark → system: a sun or a crescent for the scheme shown, with a tiny
 * "auto" when the device decides.
 */
export function ThemeToggle({
  pref,
  scheme,
  onChange,
  className = '',
}: {
  pref: ThemePref;
  scheme: Scheme;
  onChange: (pref: ThemePref) => void;
  className?: string;
}) {
  const { t } = useI18n();
  const label = themeLabel(t, pref);
  const next = nextThemePref(pref);
  const title = [`${label}.`, pref === 'system' ? t('theme.systemHint') : null, t('theme.next', { next: t(MODE[next]) })].filter(Boolean).join(' ');
  return (
    <button
      type="button"
      className={`btn-quiet gap-1 ${className}`}
      onClick={() => onChange(next)}
      aria-label={label}
      title={title}
      data-testid="theme-toggle"
      data-theme-pref={pref}
    >
      <Icon name={scheme === 'dark' ? 'moon' : 'sun'} size={15} />
      {pref === 'system' && (
        <span aria-hidden="true" className="text-2xs font-bold uppercase leading-none tracking-[0.06em] text-subtle" data-testid="theme-auto">
          {t('theme.auto')}
        </span>
      )}
    </button>
  );
}
